/**
 * フロントエンド共通ユーティリティ
 */
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
/**
 * TailwindCSS クラス名を結合し、重複を解決する
 * @param inputs - クラス名の配列（条件付き可）
 * @returns 結合されたクラス名文字列
 */
export function cn(...inputs) {
    return twMerge(clsx(inputs));
}
/**
 * URL からファイルをダウンロードする
 *
 * クロスオリジン URL でも download 属性のファイル名が正しく適用されるよう、
 * 一度 Blob としてフェッチしてから Blob URL 経由でダウンロードする
 *
 * @param url ダウンロード元 URL
 * @param fileName 保存時のファイル名
 * @throws ダウンロードに失敗した場合
 */
export async function downloadFromUrl(url, fileName) {
    let blob;
    if (url.startsWith('data:')) {
        // data: URI は fetch() すると CSP connect-src に引っかかるため直接変換する
        const [header, base64Data] = url.split(',');
        const mimeMatch = header.match(/data:([^;]+)/);
        const mimeType = mimeMatch?.[1] ?? 'application/octet-stream';
        const binaryStr = atob(base64Data);
        const bytes = Uint8Array.from(binaryStr, (c) => c.charCodeAt(0));
        blob = new Blob([bytes], { type: mimeType });
    }
    else {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Failed to fetch file: ${response.status}`);
        }
        blob = await response.blob();
    }
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
}
/**
 * ファイルサイズをフォーマット
 */
export function formatFileSize(bytes) {
    if (bytes === 0)
        return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / k ** i).toFixed(1)} ${sizes[i]}`;
}
/**
 * トークン数をフォーマット（K 表記）
 */
export function formatTokenCount(tokens) {
    if (tokens >= 1000) {
        const k = tokens / 1000;
        const formatted = Number.isInteger(k) ? k.toLocaleString() : k.toLocaleString();
        return `${formatted}K`;
    }
    return tokens.toString();
}
/**
 * 実行時間を人間が読みやすい形式にフォーマット
 */
export function formatExecutionTime(ms) {
    return `${(ms / 1000).toFixed(2)}秒`;
}
/**
 * ツールメッセージをグルーピングする
 *
 * - 同じ toolCallId を持つメッセージをペアリング
 * - toolCallId がない場合は単独のグループとして扱う
 * - 結果がない呼び出しは「実行中」として扱う
 */
export function groupToolMessages(messages) {
    const toolMessages = messages.filter((m) => m.role === 'tool');
    if (toolMessages.length === 0) {
        return [];
    }
    const groupMap = new Map();
    let generatedIdCounter = 0;
    for (const message of toolMessages) {
        const toolCallId = message.toolCallId ?? `generated-${generatedIdCounter++}`;
        if (!groupMap.has(toolCallId)) {
            groupMap.set(toolCallId, {
                toolCallId,
                toolName: message.toolName ?? 'unknown',
                args: message.toolArgs,
                intent: message.intent,
                isError: false,
                isRunning: true,
                callTimestamp: message.timestamp,
            });
        }
        const group = groupMap.get(toolCallId);
        if (!group)
            continue;
        if (message.content) {
            group.result = message.content;
            group.isRunning = false;
            group.isError = message.isError ?? false;
            group.resultTimestamp = message.timestamp;
            if (message.toolArgs && !group.args) {
                group.args = message.toolArgs;
            }
            if (message.intent && !group.intent) {
                group.intent = message.intent;
            }
        }
        else {
            if (message.toolArgs) {
                group.args = message.toolArgs;
            }
            if (message.intent) {
                group.intent = message.intent;
            }
            if (message.timestamp && !group.callTimestamp) {
                group.callTimestamp = message.timestamp;
            }
        }
        if (group.toolName === 'unknown' && message.toolName) {
            group.toolName = message.toolName;
        }
    }
    for (const group of groupMap.values()) {
        if (group.result !== undefined) {
            group.isRunning = false;
        }
    }
    return Array.from(groupMap.values()).sort((a, b) => {
        const aTime = a.callTimestamp ?? '';
        const bTime = b.callTimestamp ?? '';
        return aTime.localeCompare(bTime);
    });
}
//# sourceMappingURL=index.js.map